/** @format */

import {MutableRefObject, ReactNode, useMemo} from 'react';

import {OnChangeFn, Row, RowSelectionState} from '@tanstack/react-table';

import {Table as MuiTable, TableFooter, TableHead} from '@mui/material';

import {
  ColumnType,
  ConstantsList,
  SortDirection,
  TanStackTableData,
  TableVariant,
} from './core/types';
import {useTableModel} from './core/useTableModel';
import {TableShell} from './core/TableShell';
import {TableBodyRow} from './core/components/TableBodyRow';
import {TableHeadRow} from './core/components/TableHeadRow';
import {StyledTableBody} from './core/styles';
import {
  ARROW_CELL_WIDTH,
  CHECKBOX_CELL_WIDTH,
  COLUMN_ID_ATTRIBUTE,
  HEAD_HEIGHT,
} from './core/constants';

interface Props {
  columns: ColumnType[];
  data: TanStackTableData[];
  search?: string;
  searchByColumnIds?: string[];
  defaultSortBy?: string;
  defaultOrderBy?: SortDirection;
  enableSorting?: boolean;
  enableFilters?: boolean;
  filterOrder?: string[];
  onOpenSubRows?: (value: boolean) => void;
  onFilterChange?: (columnId: string, value: string | null | undefined) => void;
  rowSelection?: RowSelectionState;
  setRowSelection?: OnChangeFn<RowSelectionState>;
  variant?: TableVariant;
  constants?: ConstantsList;
  filterBarRef?: React.RefObject<HTMLDivElement>;
  rowIdKey?: string;
  isRowSelectionDisabled?: (row: Row<TanStackTableData>) => boolean;
  exportDataRef?: MutableRefObject<TanStackTableData[]>;
  onExportDataChange?: (dataLength: number) => void;
  filterBarActions?: ReactNode;
  // Опциональный рендерер закреплённой строки в подвале таблицы (`<tfoot>`).
  fixedFooterContent?: () => ReactNode;
  // Прилипает ли шапка таблицы к верху viewport при скролле страницы.
  stickyHeader?: boolean;
  // Прилипает ли подвал таблицы (`<tfoot>`) к низу viewport при скролле.
  stickyFooter?: boolean;
  // Ключ для сохранения пользовательских ширин колонок в localStorage.
  // Указан — колонки можно тянуть за границу, ширины переживают перезагрузку.
  // Не указан — ресайза нет, поведение прежнее.
  columnResizingKey?: string;
}

const Table = ({
  columns,
  data,
  searchByColumnIds,
  search,
  defaultOrderBy,
  defaultSortBy,
  onOpenSubRows,
  onFilterChange,
  rowSelection,
  setRowSelection,
  enableSorting = true,
  enableFilters = false,
  filterOrder = [],
  variant = 'dark',
  constants = {},
  filterBarRef,
  rowIdKey = 'id',
  isRowSelectionDisabled,
  exportDataRef,
  onExportDataChange,
  filterBarActions,
  fixedFooterContent,
  stickyHeader = false,
  stickyFooter = false,
  columnResizingKey,
}: Props) => {
  const {
    table,
    rows,
    headerGroups,
    allSubColumnIds,
    isWithSubRows,
    enableRowSelection,
    isAllSelectionDisabled,
    filters,
    filterOptions,
    handleFilterChange,
    handleResetFilters,
    isColumnResizingEnabled,
    setColumnSize,
    resetColumnSize,
  } = useTableModel({
    columns,
    data,
    search,
    searchByColumnIds,
    defaultSortBy,
    defaultOrderBy,
    enableSorting,
    enableFilters,
    filterOrder,
    onOpenSubRows,
    onFilterChange,
    rowSelection,
    setRowSelection,
    rowIdKey,
    isRowSelectionDisabled,
    exportDataRef,
    onExportDataChange,
    columnResizingKey,
  });

  const effectiveHeadHeight = constants.headHeight ?? HEAD_HEIGHT;
  const headRowHeight = headerGroups.length > 1 ? effectiveHeadHeight / 2 : effectiveHeadHeight;
  const arrowCellWidth = constants.arrowCellWidth ?? ARROW_CELL_WIDTH;
  const checkboxCellWidth = constants.checkboxCellWidth ?? CHECKBOX_CELL_WIDTH;

  // Один источник для <colgroup> и для ширины таблицы: разойтись в порядке или в числах они
  // физически не могут. Служебные колонки идут первыми и только когда реально рендерятся —
  // порядок обязан повторять порядок ячеек в строке (см. TableHeadRow/TableBodyRow).
  // Считаем только при включённом ресайзе: остальным ~9 экранам результат не нужен.
  const colWidths: {columnId?: string; width: number}[] = isColumnResizingEnabled
    ? [
        ...(isWithSubRows ? [{width: arrowCellWidth}] : []),
        ...(enableRowSelection ? [{width: checkboxCellWidth}] : []),
        ...table
          .getVisibleLeafColumns()
          .map(column => ({columnId: column.id, width: column.getSize()})),
      ]
    : [];
  const totalColumnsWidth = colWidths.reduce((total, col) => total + col.width, 0);

  const columnResizing = useMemo(
    () =>
      isColumnResizingEnabled
        ? {handleResize: setColumnSize, handleReset: resetColumnSize}
        : undefined,
    [isColumnResizingEnabled, setColumnSize, resetColumnSize],
  );

  return (
    <TableShell
      variant={variant}
      enableFilters={enableFilters}
      filters={filters}
      filterOptions={filterOptions}
      onFilterChange={handleFilterChange}
      onResetFilters={handleResetFilters}
      columns={columns}
      filterBarRef={filterBarRef}
      filterBarActions={filterBarActions}
      stickyHeader={stickyHeader}
      stickyFooter={stickyFooter}
    >
      <MuiTable
        data-testid='table'
        sx={{tableLayout: 'fixed'}}
        // Явная сумма ширин + `max-width: min-content` — единственная комбинация, при которой
        // отрендеренная ширина колонки равна заданной, в том числе во время драга (он мутирует
        // <col> без ре-рендера, поэтому inline-ширина таблицы отстаёт до pointerup). Обоснование
        // с замерами в Chrome — в README, раздел «Раскладка при включённом ресайзе».
        //
        // Значения идут в `style`, а не в `sx`: сумма меняется на каждом ресайзе, и emotion
        // плодил бы по новому классу на каждое число.
        style={
          isColumnResizingEnabled ? {width: totalColumnsWidth, maxWidth: 'min-content'} : undefined
        }
        stickyHeader={stickyHeader}
      >
        {isColumnResizingEnabled && (
          // Ширина <col> приоритетнее ширины ячейки первой строки, поэтому подколонки группы
          // получают собственные ширины, а не поделённую поровну ширину группы.
          <colgroup>
            {colWidths.map(({columnId, width}, index) => (
              <col
                key={columnId ?? `service-${index}`}
                {...(columnId ? {[COLUMN_ID_ATTRIBUTE]: columnId} : null)}
                style={{width}}
              />
            ))}
          </colgroup>
        )}
        <TableHead
          sx={
            stickyHeader
              ? {
                  // Перекрываем `position: relative` из StyledTableCell, которое иначе
                  // выигрывает у MUI's .MuiTableCell-stickyHeader (одинаковая specificity).
                  '.MuiTableCell-head': {
                    position: 'sticky',
                    zIndex: 2,
                    backgroundColor: variant === 'dark' ? '#202020' : '#F2F3F8',
                  },
                  // top для каждого ряда шапки (multi-row sticky).
                  ...Object.fromEntries(
                    headerGroups.map((_, index) => [
                      `tr:nth-of-type(${index + 1}) .MuiTableCell-head`,
                      {top: index * headRowHeight},
                    ]),
                  ),
                }
              : undefined
          }
        >
          {headerGroups.map((headerGroup, index) => (
            <TableHeadRow
              key={headerGroup.id}
              isWithSubRows={isWithSubRows}
              headerGroup={headerGroup}
              allSubColumnIds={allSubColumnIds}
              table={table}
              index={index}
              enableRowSelection={enableRowSelection}
              isAllSelectionDisabled={isAllSelectionDisabled}
              constants={constants}
              variant={variant}
              columnResizing={columnResizing}
            />
          ))}
        </TableHead>

        <StyledTableBody variant={variant}>
          {rows.map((row, index) => (
            <TableBodyRow
              key={row.id}
              data-index={index}
              isWithSubRows={isWithSubRows}
              rows={rows}
              enableRowSelection={enableRowSelection}
              isRowSelectionDisabled={isRowSelectionDisabled}
              constants={constants}
              variant={variant}
            />
          ))}
        </StyledTableBody>

        {fixedFooterContent && (
          <TableFooter
            sx={
              stickyFooter
                ? {
                    '.MuiTableCell-footer': {
                      position: 'sticky',
                      bottom: 0,
                      zIndex: 2,
                      backgroundColor: variant === 'dark' ? '#202020' : '#F2F3F8',
                    },
                  }
                : undefined
            }
          >
            {fixedFooterContent()}
          </TableFooter>
        )}
      </MuiTable>
    </TableShell>
  );
};

export {Table};
